/**
 * Query Key Factory
 * Centralized query keys for React Query to ensure consistency and type safety
 */

export const queryKeys = {
  // Auth
  auth: {
    me: () => ['/api/auth/me'] as const,
  },

  // Recipes
  recipes: {
    all: () => ['/api/recipes'] as const,
    list: (workspaceId?: string) => ['/api/recipes', { workspaceId }] as const,
    detail: (id: string) => [`/api/recipes/${id}`] as const,
    categories: () => ['/api/recipes/categories'] as const,
    overview: () => ['/api/recipes/overview'] as const,
    costs: (id: string) => [`/api/recipes/${id}/costs`] as const,
    references: (id: string) => [`/api/recipes/${id}/references`] as const,
    profitAnalysis: () => ['/api/recipes/profit-analysis'] as const,
  },

  // ChefAiQ Library
  chefaiq: {
    all: () => ['/api/chefaiq-recipes'] as const,
    detail: (id: string) => [`/api/chefaiq-recipes/${id}`] as const,
  },

  // TBA Recipes
  tba: {
    all: () => ['/api/tba-recipes'] as const,
    detail: (id: string) => [`/api/tba-recipes/${id}`] as const,
  },

  // Customers
  customers: {
    all: (workspaceId?: string) => ['customers', workspaceId] as const,
    orders: (id: string) => ['customer-orders', id] as const,
  },

  // Ingredients
  ingredients: {
    all: () => ['/api/ingredients'] as const,
    list: (workspaceId?: string) => ['/api/ingredients', { workspaceId }] as const,
    categories: () => ['/api/ingredient-categories'] as const,
  },

  // Materials
  materials: {
    all: () => ['/api/materials'] as const,
    list: (workspaceId?: string) => ['/api/materials', { workspaceId }] as const,
    categories: () => ['/api/material-categories'] as const,
  },

  // Equipment
  equipment: {
    all: () => ['/api/equipment'] as const,
    categories: () => ['/api/equipment-categories'] as const,
  },

  // Workspaces
  workspaces: {
    all: () => ['/api/workspaces'] as const,
    detail: (id: string) => [`/api/workspaces/${id}`] as const,
  },

  // Team
  team: {
    members: (accountId?: string) => ['/api/team', accountId, 'members'] as const,
    roles: (accountId?: string) => ['/api/team', accountId, 'roles'] as const,
    invitations: () => ['/api/team/invitations'] as const,
  },

  // Shopping List
  shoppingList: {
    all: () => ['shopping-list'] as const,
    byWorkspace: (workspaceId?: string) => ['shopping-list', { workspaceId }] as const,
  },

  // Orders
  orders: {
    all: () => ['/api/orders'] as const,
    detail: (id: string) => [`/api/orders/${id}`] as const,
    analytics: () => ['/api/orders/analytics'] as const,
  },

  // Inventory

  // Starter Pack

  // Subscriptions
  subscriptions: {
    current: () => ['/api/subscriptions/current'] as const,
    plans: () => ['/api/admin/subscription-plans'] as const,
    checkoutStatus: () => ['/api/subscription-checkout/status'] as const,
  },


  // Account
  account: {
    details: () => ['/api/account'] as const,
  },

  // Payments
  payments: {
    settings: () => ['/api/payment-settings/enabled'] as const,
    transactions: () => ['/api/payments/transactions'] as const,
  },

  // Product Packages
  productPackages: {
    all: () => ['/api/product-packages'] as const,
    byItem: (itemType: string, itemId: string) => ['/api/product-packages', itemType, itemId] as const,
  },

  // Suppliers
  suppliers: {
    byItem: (itemId: string, itemType: string) => ['/api/suppliers', itemId, itemType] as const,
  },

  // Workspace Preferences
  workspacePreferences: {
    byWorkspace: (workspaceId?: string) => ['/api/workspace/preferences', workspaceId] as const,
  },

  // Inventory Analytics
  inventoryAnalytics: {
    lowStock: (teamId?: string) => ['/api/inventory/low-stock', { teamId }] as const,
    expiring: (teamId?: string) => ['/api/inventory/expiring', { teamId }] as const,
    trends: (teamId?: string) => ['/api/inventory/analytics/trends', { teamId }] as const,
  },

  // Analytics
  analytics: {
    overview: () => ['/api/analytics/overview'] as const,
  },

  // Starter Pack
  starterPack: {
    ingredients: () => ['/api/starter-pack/ingredients', { v: 2 }] as const,
    materials: () => ['/api/starter-pack/materials'] as const,
    equipment: () => ['/api/starter-pack/equipment'] as const,
    recipeCategories: () => ['/api/starter-pack/categories/recipes'] as const,
  },

  // Landing Page
  landingPage: {
    section: (section: string) => ['landing-page', section] as const,
  },

  // Helpdesk
  helpdesk: {
    tickets: () => ['helpdesk-tickets'] as const,
    ticketsAdmin: () => ['helpdesk-tickets-admin'] as const,
    stats: () => ['helpdesk-stats'] as const,
    detail: (id?: string) => ['helpdesk-ticket', id] as const,
  },

  // Inventory
  inventory: {
    locations: () => ['inventory-locations'] as const,
    items: () => ['inventory-items'] as const,
    itemsForBreakdown: () => ['inventory-items-for-breakdown'] as const,
  },

  // Vouchers
  vouchers: {
    all: () => ['/api/vouchers'] as const,
    stats: (voucherId: string) => ['/api/vouchers', voucherId, 'stats'] as const,
  },

  // Admin
  admin: {
    users: (params?: { search?: string; showDeleted?: boolean }) => 
      ['/api/admin/users', params] as const,
    subscriptionPlans: () => ['/api/admin/subscription-plans'] as const,
  },

  // AI
  ai: {
    promptUsage: () => ['/api/ai-usage/prompt'] as const,
    knowledgeBase: () => ['/api/knowledge-base'] as const,
  },

  // Menus
  menus: {
    list: () => ['/api/menus'] as const,
    detail: (id: string) => [`/api/menus/${id}`] as const,
  },
} as const;
